/**
 * Componente Payment Brick - Mercado Pago
 * 
 * Integração com Checkout Bricks do Mercado Pago
 * 
 * @author Sophya Sistema
 * @date 12/11/2025
 */

import React, { useEffect, useState, useRef } from 'react';
import { api } from '../services/api';

interface PaymentBrickProps {
  valor: number;
  descricao: string;
  consultaId?: number;
  pacienteId?: number;
  onSuccess: (paymentId: number, status: string, paymentData?: any) => void;
  onError: (error: any) => void;
}

declare global {
  interface Window {
    MercadoPago: any;
    paymentBrickController: any;
  }
}

export const MercadoPagoPaymentBrick: React.FC<PaymentBrickProps> = ({
  valor,
  descricao,
  consultaId,
  pacienteId,
  onSuccess,
  onError
}) => {
  const [publicKey, setPublicKey] = useState('');
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');
  const brickRef = useRef<any>(null);

  // Buscar Public Key
  useEffect(() => {
    const buscarConfig = async () => {
      try {
        console.log('[MP-BRICK] Buscando configuração...');
        
        const response = await api.get('/mercadopago/config');
        
        if (response.data.sucesso) {
          setPublicKey(response.data.publicKey);
          console.log('[MP-BRICK] ✅ Public Key obtida');
        } else {
          setErro(response.data.mensagem);
        }
      } catch (error: any) {
        console.error('[MP-BRICK] Erro ao buscar config:', error);
        setErro('Erro ao carregar configuração de pagamento');
      }
    };

    buscarConfig();
  }, []);

  // Carregar SDK e renderizar Brick
  useEffect(() => {
    if (!publicKey) return;

    const carregarSDK = () => {
      // Verificar se SDK já está carregado
      if (window.MercadoPago) {
        console.log('[MP-BRICK] SDK já carregado');
        renderizarBrick();
        return;
      }

      console.log('[MP-BRICK] Carregando SDK...');

      // Carregar SDK do Mercado Pago
      const script = document.createElement('script');
      // URL oficial do SDK v2 dos Checkout Bricks
      script.src = 'https://sdk.mercadopago.com/js/v2';
      script.async = true;
      
      script.onload = () => {
        console.log('[MP-BRICK] ✅ SDK carregado');
        renderizarBrick();
      };
      
      script.onerror = () => {
        console.error('[MP-BRICK] ❌ Erro ao carregar SDK');
        setErro('Erro ao carregar SDK do Mercado Pago');
        setLoading(false);
      };
      
      document.body.appendChild(script);
    };

    const renderizarBrick = async () => {
      try {
        console.log('[MP-BRICK] Inicializando Mercado Pago...');
        
        // Inicializar Mercado Pago
        const mp = new window.MercadoPago(publicKey);
        
        // Configurar opções do Brick
        const bricksBuilder = mp.bricks();
        
        // Renderizar Payment Brick
        const brickController = await bricksBuilder.create('payment', 'payment-brick-container', {
          initialization: {
            amount: valor
            // Não definir payer aqui - será preenchido pelo usuário no formulário
          },
          customization: {
            visual: {
              style: {
                theme: 'default', // ou 'dark', 'bootstrap', 'flat'
              }
            },
            paymentMethods: {
              ticket: 'all',
              bankTransfer: 'all',
              creditCard: 'all',
              debitCard: 'all',
              maxInstallments: 12,
              minInstallments: 1
            }
          },
          callbacks: {
            onReady: () => {
              console.log('[MP-BRICK] ✅ Payment Brick pronto');
              setLoading(false);
            },
            onSubmit: async ({ selectedPaymentMethod, formData }: any) => {
              console.log('[MP-BRICK] Processando pagamento...', {
                selectedPaymentMethod,
                formData
              });
              
              try {
                // Enviar dados para o backend (incluindo dados completos do payer)
                const response = await api.post('/mercadopago/payment', {
                  token: formData.token,
                  issuerId: formData.issuer_id,
                  paymentMethodId: formData.payment_method_id,
                  transactionAmount: valor,
                  installments: formData.installments,
                  description: descricao,
                  payer: {
                    email: formData.payer.email,
                    first_name: formData.payer.first_name,
                    last_name: formData.payer.last_name,
                    identification: formData.payer.identification,
                    address: formData.payer.address
                  },
                  consultaId,
                  pacienteId
                });
                
                if (response.data.sucesso) {
                  console.log('[MP-BRICK] ✅ Pagamento aprovado:', response.data);
                  // Retornar objeto completo para permitir acesso a todos os dados
                  onSuccess(response.data.paymentId!, response.data.status!, response.data.paymentData);
                } else {
                  throw new Error(response.data.mensagem);
                }
              } catch (error: any) {
                console.error('[MP-BRICK] ❌ Erro ao processar:', error);
                onError(error);
              }
            },
            onError: (error: any) => {
              // Filtrar erros não críticos conhecidos
              const isNonCritical = error?.type === 'non_critical';
              const isAddressError = error?.cause === 'get_address_data_failed';
              
              if (isNonCritical && isAddressError) {
                // Erro 401 CEP - Não crítico, apenas loga como info
                console.info('[MP-BRICK] ℹ️ Autocomplete de CEP indisponível (token expirado) - Pagamento continua funcionando');
                return; // Não chama onError para não mostrar mensagem ao usuário
              }
              
              // Outros erros críticos devem ser tratados
              console.error('[MP-BRICK] ❌ Erro no brick:', error);
              onError(error);
            }
          }
        });
        
        // Salvar controller para unmount
        brickRef.current = brickController;
        
        console.log('[MP-BRICK] ✅ Brick renderizado');
        
      } catch (error: any) {
        console.error('[MP-BRICK] ❌ Erro ao renderizar:', error);
        setErro('Erro ao renderizar formulário de pagamento');
        setLoading(false);
      }
    };

    carregarSDK();

    // Cleanup ao desmontar
    return () => {
      if (brickRef.current) {
        try {
          brickRef.current.unmount();
          console.log('[MP-BRICK] Brick desmontado');
        } catch (e) {
          console.warn('[MP-BRICK] Erro ao desmontar brick:', e);
        }
      }
    };
  }, [publicKey, valor, descricao, consultaId, pacienteId, onSuccess, onError]);

  if (erro) {
    return (
      <div className="alert alert-danger" role="alert">
        <i className="fas fa-exclamation-triangle me-2"></i>
        {erro}
      </div>
    );
  }

  return (
    <div className="mercadopago-payment-brick">
      {loading && (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Carregando...</span>
          </div>
          <p className="mt-3 text-muted">Carregando formulário de pagamento...</p>
        </div>
      )}
      
      {/* Container onde o Brick será renderizado */}
      <div id="payment-brick-container"></div>
    </div>
  );
};
